import React, { useState, useEffect } from 'react';
import { VisionResponses, DomainResult, TestResult } from '../../types/index';

// WHO标准视力测试图片样式
const visionTestImageStyles = {
  base: {
    objectFit: 'contain' as const,
    imageRendering: 'crisp-edges' as const,
    display: 'block',
    margin: '0 auto',
    border: 'none',
    outline: 'none'
  },
  largeE: {
    // WHO标准6/60大E字符: 4.4cm（当前显示5.1cm，需要进一步减小到约3.8cm来达到4.4cm目标）
    width: '3.8cm',
    height: 'auto',
    maxWidth: '90vw', // 防止在小屏幕上溢出
    maxHeight: '60vh'
  },
  mediumERow: {
    // WHO标准6/12中号E字符: 0.85cm，从显示0.6cm调整到0.85cm
    // 假设一行有5个E字符加间距，总宽度需要增大到约7cm
    width: '7cm',
    height: 'auto',
    maxWidth: '95vw',
    maxHeight: '40vh'
  },
  nearVisionText: {
    // WHO标准N6近视力E字符: 0.1cm，但这也是一行，总宽度约为1cm
    width: '1cm',
    height: 'auto',
    maxWidth: '95vw',
    maxHeight: '30vh',
    minHeight: '2px' // 确保最小可见性，但调整为更小
  }
};

interface VisionAssessmentProps {
  onComplete: (result: DomainResult) => void;
  onNext: () => void;
}

type VisionStep = 
  | 'intro' 
  | 'filter1' 
  | 'filter2'
  | 'assessment_mode_choice'
  | 'external_eye_check'
  | 'large_e_test'
  | 'medium_e_test'
  | 'eye_transition'
  | 'near_vision_transition'
  | 'near_vision_test'
  | 'results' 
  | 'carer_correction'
  | 'completed';

type AssessmentMode = 'independent' | 'assisted' | 'skip';

const VisionAssessment: React.FC<VisionAssessmentProps> = ({ onComplete, onNext }) => {
  const [currentStep, setCurrentStep] = useState<VisionStep>('intro');
  const [responses, setResponses] = useState<Partial<VisionResponses>>({});
  const [assessmentMode, setAssessmentMode] = useState<AssessmentMode>('independent');
  
  // 照护者修正结果
  const [carerCorrection, setCarerCorrection] = useState({
    externalEyeCorrect: null as boolean | null,
    largeECorrect: null as boolean | null,      // 大E字符测试修正
    mediumECorrect: null as boolean | null,     // 中号E字符测试修正
    nearVisionCorrect: null as boolean | null
  });

  // 左右眼测试状态
  const [currentEye, setCurrentEye] = useState<'left' | 'right'>('right'); // 先测右眼
  // const [currentTestType, setCurrentTestType] = useState<'large_e' | 'medium_e'>('large_e'); // Removed: not used in current implementation
  
  // 大E字符测试状态 - 分左右眼
  const [largeEIndex, setLargeEIndex] = useState(0);
  const [leftEyeLargeEResults, setLeftEyeLargeEResults] = useState<string[]>([]);
  const [rightEyeLargeEResults, setRightEyeLargeEResults] = useState<string[]>([]);
  
  // 中号E字符测试状态 - 分左右眼
  const [leftEyeMediumECount, setLeftEyeMediumECount] = useState<number | null>(null);
  const [rightEyeMediumECount, setRightEyeMediumECount] = useState<number | null>(null);
  
  // 近视力测试状态
  const [nearVisionECount, setNearVisionECount] = useState<number | null>(null);

  // 大E字符测试数据
  const largeEChars = [
    { file: 'e_large_right.png', direction: 'right', label: '开口向右', answer: '→' },
    { file: 'e_large_left.png', direction: 'left', label: '开口向左', answer: '←' },
    { file: 'e_large_up.png', direction: 'up', label: '开口向上', answer: '↑' },
    { file: 'e_large_down.png', direction: 'down', label: '开口向下', answer: '↓' }
  ];

  // --- Evaluation Logic ---
  const evaluateVision = (): DomainResult => {
    // 1) Filter questions take priority - 如果未通过，整体失败，只显示筛查结果
    if (responses.symptomsFilter || responses.riskFactorsFilter) {
      return { 
        status: 'FAIL', 
        completionMethod: 'filter_question',
        filterQuestionResult: false, // 标记filter question未通过
        testResults: [
          {
            name: "视力问题筛查",
            passed: false,
            details: responses.symptomsFilter ? "视力问题症状筛查：存在问题" : "视力问题风险筛查：存在风险",
            rawUserAnswers: {
              filterQuestion: true,
              symptomsFilter: responses.symptomsFilter,
              riskFactorsFilter: responses.riskFactorsFilter
            }
          }
        ],
        recommendations: ["ICOPE-C3-S3.1.5-Community-Care-Overview", "ICOPE-C8-S8.2-Community-Care-Vision"],
        // 🎯 双数据源支持：筛查问题失败时也添加responses字段
        responses: {
          filterQuestion: true,
          symptomsFilter: responses.symptomsFilter,
          riskFactorsFilter: responses.riskFactorsFilter
        }
      };
    }
    
    // If assessment was skipped
    if (assessmentMode === 'skip') {
      return {
        status: 'INCONCLUSIVE',
        completionMethod: 'test_skipped',
        filterQuestionResult: true, // 筛查通过但跳过了测试
        recommendations: ["ICOPE-C3-S3.1.5-Community-Care-Overview", "ICOPE-C8-S8.2-Community-Care-Vision"]
      };
    }
    
    // 如果到了这里，说明filter questions都通过了
    const filterQuestionsPassed = true;
    
    // 2) External eye check - 如果发现问题，整体失败
    const externalEyeCheckPassed = responses.externalEyeCheck !== false;
    if (!externalEyeCheckPassed) {
      return {
        status: 'FAIL',
        completionMethod: 'full_test',
        filterQuestionResult: filterQuestionsPassed,
        testResults: [
          {
            name: "外部眼检",
            passed: false,
            details: "发现眼部外观异常"
          }
        ],
        recommendations: ["ICOPE-C3-S3.1.5-Community-Care-Overview", "ICOPE-C8-S8.2-Community-Care-Vision"]
      };
    }
    
    // 3) 远视力测试评估 - 左右眼分别评估
    // 右眼大E字符测试 (6/60) - 需要≥2个正确
    const rightEyeLargeECorrect = rightEyeLargeEResults.filter((result, index) => 
      result === largeEChars[index].direction
    ).length;
    const rightEyeLargeEPassed = rightEyeLargeECorrect >= 2;
    
    // 左眼大E字符测试 (6/60) - 需要≥2个正确
    const leftEyeLargeECorrect = leftEyeLargeEResults.filter((result, index) => 
      result === largeEChars[index].direction
    ).length;
    const leftEyeLargeEPassed = leftEyeLargeECorrect >= 2;
    
    // 右眼中号E字符测试 (6/12) - 需要≥3个正确，但只有在大E通过时才进行
    const rightEyeMediumEPassed = rightEyeLargeEPassed ? (rightEyeMediumECount ?? 0) >= 3 : false;
    
    // 左眼中号E字符测试 (6/12) - 需要≥3个正确，但只有在大E通过时才进行
    const leftEyeMediumEPassed = leftEyeLargeEPassed ? (leftEyeMediumECount ?? 0) >= 3 : false;
    
    // 任意眼睛的远视力测试失败，整体失败
    const rightEyeDistancePassed = rightEyeLargeEPassed && rightEyeMediumEPassed;
    const leftEyeDistancePassed = leftEyeLargeEPassed && leftEyeMediumEPassed;
    
    // 4) 近视力测试评估 (N6) - 需要≥3个正确
    const nearVisionPassed = (nearVisionECount ?? 0) >= 3;
    
    // 视力评估通过标准：所有测试都必须通过才算通过
    const allTestsPassed = externalEyeCheckPassed && rightEyeDistancePassed && leftEyeDistancePassed && nearVisionPassed;
    
    // 构建完整测试结果 - 只有在进行了实际测试时才包含
    const testResults: TestResult[] = [
      { 
        name: "视力问题筛查",
        passed: true,
        details: "筛查问题：无视力问题症状和风险",
        rawUserAnswers: {
          filterQuestion: true,
          symptomsFilter: responses.symptomsFilter,
          riskFactorsFilter: responses.riskFactorsFilter
        }
      },
      { 
        name: "外部眼检", 
        passed: externalEyeCheckPassed,
        details: "眼部外观检查正常",
        rawUserAnswers: {
          externalEyeCheckPassed: externalEyeCheckPassed
        }
      },
      { 
        name: "右眼大E字符测试 (6/60)", 
        passed: rightEyeLargeEPassed, 
        details: `右眼正确识别 ${rightEyeLargeECorrect}/4 个大E字符方向`,
        rawUserAnswers: {
          rightEyeLargeECorrect: rightEyeLargeECorrect,
          rightEyeLargeEPassed: rightEyeLargeEPassed
        }
      },
      { 
        name: "左眼大E字符测试 (6/60)", 
        passed: leftEyeLargeEPassed, 
        details: `左眼正确识别 ${leftEyeLargeECorrect}/4 个大E字符方向`,
        rawUserAnswers: {
          leftEyeLargeECorrect: leftEyeLargeECorrect,
          leftEyeLargeEPassed: leftEyeLargeEPassed
        }
      },
      { 
        name: "右眼中号E字符测试 (6/12)", 
        passed: rightEyeMediumEPassed, 
        details: `右眼看清 ${rightEyeMediumECount ?? 0}/5 个中号E字符方向`,
        rawUserAnswers: {
          rightEyeMediumECount: rightEyeMediumECount,
          rightEyeMediumEPassed: rightEyeMediumEPassed
        }
      },
      { 
        name: "左眼中号E字符测试 (6/12)", 
        passed: leftEyeMediumEPassed, 
        details: `左眼看清 ${leftEyeMediumECount ?? 0}/5 个中号E字符方向`,
        rawUserAnswers: {
          leftEyeMediumECount: leftEyeMediumECount,
          leftEyeMediumEPassed: leftEyeMediumEPassed
        }
      },
      { 
        name: "近视力测试 (N6)", 
        passed: nearVisionPassed,
        details: `40厘米距离识别 ${nearVisionECount ?? 0}/5 个E字符方向`,
        rawUserAnswers: {
          nearVisionECount: nearVisionECount,
          nearVisionPassed: nearVisionPassed
        }
      }
    ];

    return {
      status: allTestsPassed ? 'PASS' : 'FAIL',
      completionMethod: assessmentMode === 'assisted' ? 'assisted_test' : 'independent_test',
      filterQuestionResult: filterQuestionsPassed, // 记录筛查问题通过了
      testResults,
      recommendations: allTestsPassed ? 
        ["ICOPE-C3-S3.1.3-Advice-EyeHealth"] : 
        ["ICOPE-C3-S3.1.5-Community-Care-Overview", "ICOPE-C8-S8.2-Community-Care-Vision"]
    };
  };

  // --- Step Handlers ---
  const handleFilterResponse = (filter: 'symptomsFilter' | 'riskFactorsFilter', value: boolean) => {
    const updatedResponses = { ...responses, [filter]: value };
    setResponses(updatedResponses);
    if (value) {
      // 筛查问题失败，使用更新后的数据立即生成结果
      const result = {
        status: 'FAIL' as const,
        completionMethod: 'filter_question' as const,
        filterQuestionResult: false, // 标记filter question未通过
        testResults: [{
          name: "视力问题筛查",
          passed: false,
          details: filter === 'symptomsFilter' ? "视力问题症状筛查：存在问题" : "视力问题风险筛查：存在风险",
          rawUserAnswers: {
            filterQuestion: false,
            symptomsFilter: filter === 'symptomsFilter' ? true : updatedResponses.symptomsFilter,
            riskFactorsFilter: filter === 'riskFactorsFilter' ? true : updatedResponses.riskFactorsFilter
          }
        }],
        recommendations: ["ICOPE-C3-S3.1.5-Community-Care-Overview", "ICOPE-C8-S8.2-Community-Care-Vision"]
      };
      console.log('📋 视力筛查问题失败，完成评估:', result);
      onComplete(result);
      setCurrentStep('completed');
    } else {
      setCurrentStep(filter === 'symptomsFilter' ? 'filter2' : 'assessment_mode_choice');
    }
  };

  const handleModeChoice = (mode: AssessmentMode) => {
    setAssessmentMode(mode);
    if (mode === 'skip') {
      // 跳过测试，直接生成结果
      const result = {
        status: 'INCONCLUSIVE' as const,
        completionMethod: 'test_skipped' as const,
        filterQuestionResult: true, // 筛查通过但跳过了测试
        recommendations: ["ICOPE-C3-S3.1.5-Community-Care-Overview", "ICOPE-C8-S8.2-Community-Care-Vision"]
      };
      console.log('📋 视力测试跳过，完成评估:', result);
      onComplete(result);
      setCurrentStep('completed');
    } else {
      // 无论独立还是协助模式，都进入相同的测试流程
      setCurrentStep('external_eye_check');
    }
  };

  const handleExternalEyeCheck = (isPassed: boolean) => {
    setResponses(prev => ({ ...prev, externalEyeCheck: isPassed }));
    // 无论独立还是协助模式，都进入相同的大E字符测试
    setCurrentStep('large_e_test');
  };

  // handleDistanceVision函数已不再使用，因为我们重构了测试流程

  // const handleNearVision = (isPassed: boolean) => {
  //   setResponses(prev => ({ ...prev, nearVision: { readCount: isPassed ? 1 : 0, isPassed } }));
  //   setCurrentStep('results');
  // }; // Removed: not used in current implementation

  // 处理测试结果确认
  const handleResultsConfirm = () => {
    const result = evaluateVision();
    onComplete(result);
    setCurrentStep('completed');
  };

  // 处理照护者修正
  const handleCarerCorrection = (externalEye: boolean, largeE: boolean, mediumE: boolean, nearVision: boolean) => {
    setCarerCorrection({ 
      externalEyeCorrect: externalEye, 
      largeECorrect: largeE,
      mediumECorrect: mediumE,
      nearVisionCorrect: nearVision 
    });
    
    // 基于照护者修正重新评估 - 所有测试都必须通过
    const allTestsPassed = externalEye && largeE && mediumE && nearVision;
    
    const testResults: TestResult[] = [
      { 
        name: "外部眼检", 
        passed: externalEye, 
        details: "照护者确认眼部外观检查",
        carerCorrected: true,
        rawUserAnswers: {
          externalEyeCheckPassed: externalEye,
          carerCorrected: true
        }
      },
      { 
        name: "大E字符测试 (6/60)", 
        passed: largeE, 
        details: "照护者确认大E字符识别能力",
        carerCorrected: true,
        rawUserAnswers: {
          rightEyeLargeEPassed: largeE,
          leftEyeLargeEPassed: largeE,
          carerCorrected: true
        }
      },
      { 
        name: "中号E字符测试 (6/12)", 
        passed: mediumE, 
        details: "照护者确认中号E字符识别能力",
        carerCorrected: true,
        rawUserAnswers: {
          rightEyeMediumEPassed: mediumE,
          leftEyeMediumEPassed: mediumE,
          carerCorrected: true
        }
      },
      { 
        name: "近视力测试 (N6)", 
        passed: nearVision, 
        details: "照护者确认近距离E字符识别能力",
        carerCorrected: true,
        rawUserAnswers: {
          nearVisionPassed: nearVision,
          carerCorrected: true
        }
      }
    ];

    const result: DomainResult = {
      status: allTestsPassed ? 'PASS' : 'FAIL',
      completionMethod: 'carer_corrected',
      filterQuestionResult: true, // 🎯 修复缺失：照护者修正说明筛查通过
      testResults,
      recommendations: allTestsPassed ? 
        ["ICOPE-C3-S3.1.3-Advice-EyeHealth"] : 
        ["ICOPE-C3-S3.1.5-Community-Care-Overview", "ICOPE-C8-S8.2-Community-Care-Vision"]
    };
    
    onComplete(result);
    setCurrentStep('completed');
  };

  useEffect(() => {
    if (currentStep === 'completed') {
      const timer = setTimeout(() => onNext(), 1500);
      return () => clearTimeout(timer);
    }
  }, [currentStep]);


  // --- Render Methods ---
  const renderIntro = () => (
    <div className="text-center space-y-6 p-6">
      <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-gray-900">视力健康评估</h2>
      <p className="text-gray-600 max-w-md mx-auto">我们将通过筛查问题和简单测试评估您的视力状况。这有助于及早发现视力问题。</p>
      <button 
        onClick={() => setCurrentStep('filter1')} 
        className="w-full bg-purple-600 text-white py-3 px-6 rounded-lg hover:bg-purple-700 transition-colors font-medium"
      >
        开始评估
      </button>
    </div>
  );

  const renderFilter = (isSymptom: boolean) => (
    <div className="p-6 space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {isSymptom ? '视力症状筛查' : '风险因素筛查'}
        </h2>
        <p className="text-gray-600">请诚实回答，这有助于我们为您提供最合适的建议</p>
      </div>
      
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
        <p className="font-semibold text-lg mb-4 text-gray-900">
          {isSymptom ? '您的眼睛有任何问题吗？' : '您是否有以下情况？'}
        </p>
        
        <div className="bg-white rounded-lg p-4 mb-6">
          <ul className="space-y-2 text-gray-700">
            {isSymptom ? (
              <>
                <li className="flex items-start space-x-2">
                  <span className="text-purple-500 mt-1">•</span>
                  <span>看远处或近处有困难（即使戴眼镜）</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-purple-500 mt-1">•</span>
                  <span>眼痛或眼部不适</span>
                </li>
              </>
            ) : (
              <>
                <li className="flex items-start space-x-2">
                  <span className="text-purple-500 mt-1">•</span>
                  <span>患有糖尿病或高血压</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-purple-500 mt-1">•</span>
                  <div>
                    <span>目前正在使用类固醇或眼部药物</span>
                    <div className="text-xs text-gray-500 mt-1">
                      （类固醇：如强的松、地塞米松等激素类药物；眼部药物：如眼药水、眼药膏等）
                    </div>
                  </div>
                </li>
              </>
            )}
          </ul>
        </div>
        
        <div className="space-y-3">
          <button 
            onClick={() => handleFilterResponse(isSymptom ? 'symptomsFilter' : 'riskFactorsFilter', true)} 
            className="w-full p-4 bg-red-100 border-2 border-red-200 rounded-lg text-left hover:bg-red-200 transition-colors"
          >
            <span className="font-medium text-red-800">是的，我有上述情况</span>
          </button>
          <button 
            onClick={() => handleFilterResponse(isSymptom ? 'symptomsFilter' : 'riskFactorsFilter', false)} 
            className="w-full p-4 bg-green-100 border-2 border-green-200 rounded-lg text-left hover:bg-green-200 transition-colors"
          >
            <span className="font-medium text-green-800">没有，我没有上述情况</span>
          </button>
        </div>
      </div>
    </div>
  );

  const renderAssessmentModeChoice = () => (
    <div className="p-6 space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">选择评估方式</h2>
        <p className="text-gray-600">请选择最适合您的评估方式</p>
      </div>
      
      <div className="space-y-4">
        <button
          onClick={() => handleModeChoice('independent')}
          className="w-full p-6 bg-blue-50 border-2 border-blue-200 rounded-lg text-left hover:bg-blue-100 transition-colors"
        >
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-blue-900 mb-2">我想独立完成测试</h3>
              <p className="text-sm text-blue-700">适合能够独立阅读屏幕和进行简单操作的用户</p>
            </div>
          </div>
        </button>
        
        <button
          onClick={() => handleModeChoice('assisted')}
          className="w-full p-6 bg-green-50 border-2 border-green-200 rounded-lg text-left hover:bg-green-100 transition-colors"
        >
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-green-900 mb-2">我需要家人或照护者帮助</h3>
              <p className="text-sm text-green-700">家人或照护者将协助您完成视力测试</p>
            </div>
          </div>
        </button>
        
        <button
          onClick={() => handleModeChoice('skip')}
          className="w-full p-4 bg-gray-50 border border-gray-200 rounded-lg text-center hover:bg-gray-100 transition-colors"
        >
          <span className="text-gray-600">暂时跳过视力测试</span>
        </button>
      </div>
    </div>
  );

  // 原先的辅助模式专用函数已删除，因为现在两种模式使用相同的测试流程

  const renderExternalEyeCheck = () => (
    <div className="p-6 space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">外部眼检</h2>
        <p className="text-gray-600">请观察您的眼部是否有明显异常</p>
      </div>
      
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="font-semibold text-gray-900 mb-4">📋 检查步骤：</h3>
        <div className="bg-white rounded-lg p-4 mb-4">
          <div className="flex items-start space-x-3">
            <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-sm font-medium text-blue-600">1</span>
            <p className="text-gray-700">在充足的光线下，对着镜子观察您的眼部</p>
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 mb-4">
          <div className="flex items-start space-x-3">
            <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-sm font-medium text-blue-600">2</span>
            <p className="text-gray-700">参考下方图片，检查您的眼部是否有异常情况</p>
          </div>
        </div>
        
        <h3 className="font-semibold text-gray-900 mb-4">🖼️ 异常情况参考图示：</h3>
        
        <div className="grid grid-cols-1 gap-4 mb-6">
          {/* 眼睑边缘异常 */}
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="md:w-1/3">
                <img 
                  src="/images/assessment/abnormal_external_eye_eyelid_margin.png" 
                  alt="眼睑边缘异常示例图" 
                  className="w-full h-24 object-cover rounded-lg"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
              <div className="md:w-2/3">
                <h4 className="font-medium text-gray-900 mb-2">眼睑边缘异常</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• 眼睑边缘有结痂或脓液</li>
                  <li>• 眼睑边缘红肿或疼痛</li>
                </ul>
              </div>
            </div>
          </div>
          
          {/* 眼睑闭合异常 */}
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="md:w-1/3">
                <img 
                  src="/images/assessment/abnormal_external_eye_lid_closure.png" 
                  alt="眼睑闭合异常示例图" 
                  className="w-full h-24 object-cover rounded-lg"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
              <div className="md:w-2/3">
                <h4 className="font-medium text-gray-900 mb-2">眼睑闭合异常</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• 眼睑无法完全闭合</li>
                  <li>• 眼睑下垂明显</li>
                </ul>
              </div>
            </div>
          </div>
          
          {/* 睫毛异常 */}
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="md:w-1/3">
                <img 
                  src="/images/assessment/abnormal_external_eye_eyeslashes.png" 
                  alt="睫毛异常示例图" 
                  className="w-full h-24 object-cover rounded-lg"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
              <div className="md:w-2/3">
                <h4 className="font-medium text-gray-900 mb-2">睫毛异常</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• 睫毛内翻，刺激眼球</li>
                  <li>• 睫毛大量脱落</li>
                </ul>
              </div>
            </div>
          </div>
          
          {/* 眼白异常发红 */}
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="md:w-1/3">
                <img 
                  src="/images/assessment/abnormal_external_eye_white_part_files.png" 
                  alt="眼白异常发红示例图" 
                  className="w-full h-24 object-cover rounded-lg"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
              <div className="md:w-2/3">
                <h4 className="font-medium text-gray-900 mb-2">眼白异常发红</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• 眼白部分明显发红</li>
                  <li>• 有血丝或出血点</li>
                </ul>
              </div>
            </div>
          </div>
          
          {/* 有色部分异常 */}
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="md:w-1/3">
                <img 
                  src="/images/assessment/abnormal_external_eye_coloured_part.png" 
                  alt="有色部分异常示例图" 
                  className="w-full h-24 object-cover rounded-lg"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
              <div className="md:w-2/3">
                <h4 className="font-medium text-gray-900 mb-2">有色部分异常</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• 瞳孔大小不对称</li>
                  <li>• 虹膜颜色变浑浊</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <div className="flex items-start space-x-2">
            <svg className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="font-medium text-yellow-800">温馨提示</p>
              <p className="text-yellow-700 text-sm mt-1">
                如果您不确定是否异常，建议选择"发现异常"，以便获得更全面的评估建议
              </p>
            </div>
          </div>
        </div>
        
        <div className="space-y-3">
          <button
            onClick={() => handleExternalEyeCheck(false)}
            className="w-full p-4 bg-red-100 border-2 border-red-200 rounded-lg text-left hover:bg-red-200 transition-colors"
          >
            <span className="font-medium text-red-800">是的，我发现有上述异常</span>
          </button>
          <button
            onClick={() => handleExternalEyeCheck(true)}
            className="w-full p-4 bg-green-100 border-2 border-green-200 rounded-lg text-left hover:bg-green-200 transition-colors"
          >
            <span className="font-medium text-green-800">没有，我的眼部外观正常</span>
          </button>
        </div>
      </div>
    </div>
  );

  const renderResults = () => {
    const result = evaluateVision();
    const externalEyeCheckPassed = responses.externalEyeCheck !== false;
    
    // 右眼测试结果
    const rightEyeLargeECorrect = rightEyeLargeEResults.filter((result, index) => 
      result === largeEChars[index].direction
    ).length;
    const rightEyeLargeEPassed = rightEyeLargeECorrect >= 2;
    const rightEyeMediumEPassed = rightEyeLargeEPassed ? (rightEyeMediumECount ?? 0) >= 3 : false;
    
    // 左眼测试结果
    const leftEyeLargeECorrect = leftEyeLargeEResults.filter((result, index) => 
      result === largeEChars[index].direction
    ).length;
    const leftEyeLargeEPassed = leftEyeLargeECorrect >= 2;
    const leftEyeMediumEPassed = leftEyeLargeEPassed ? (leftEyeMediumECount ?? 0) >= 3 : false;
    
    // 近视力测试结果
    const nearVisionPassed = (nearVisionECount ?? 0) >= 3;
    
    const overallPassed = result.status === 'PASS';

    return (
      <div className="p-6 space-y-6">
        <div className="text-center">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
            overallPassed ? 'bg-green-100' : 'bg-yellow-100'
          }`}>
            <svg className={`w-8 h-8 ${overallPassed ? 'text-green-600' : 'text-yellow-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">WHO标准视力测试结果</h2>
          <p className="text-gray-600">基于WHO ICOPE视力筛查标准</p>
        </div>

        <div className="space-y-4">
          {/* 外部眼检结果 */}
          <div className={`p-4 rounded-lg border ${externalEyeCheckPassed ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <h3 className="font-semibold text-gray-900 mb-2">外部眼检</h3>
            <p className="text-sm">
              结果：{externalEyeCheckPassed ? '眼部外观正常' : '发现眼部异常'} {externalEyeCheckPassed ? '✅' : '❌'}
            </p>
          </div>

          {/* 右眼测试结果 */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3">右眼远视力测试</h3>
            
            {/* 右眼大E测试 */}
            <div className={`p-3 mb-2 rounded border ${rightEyeLargeEPassed ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">大E字符 (6/60)</span>
                <span className="text-sm">
                  {rightEyeLargeECorrect}/4个 {rightEyeLargeEPassed ? '✅' : '❌'}
                </span>
              </div>
            </div>
            
            {/* 右眼中E测试 */}
            <div className={`p-3 rounded border ${rightEyeMediumEPassed ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">中号E字符 (6/12)</span>
                <span className="text-sm">
                  {rightEyeMediumECount ?? 0}/5个 {rightEyeMediumEPassed ? '✅' : '❌'}
                </span>
              </div>
            </div>
          </div>

          {/* 左眼测试结果 */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3">左眼远视力测试</h3>
            
            {/* 左眼大E测试 */}
            <div className={`p-3 mb-2 rounded border ${leftEyeLargeEPassed ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">大E字符 (6/60)</span>
                <span className="text-sm">
                  {leftEyeLargeECorrect}/4个 {leftEyeLargeEPassed ? '✅' : '❌'}
                </span>
              </div>
            </div>
            
            {/* 左眼中E测试 */}
            <div className={`p-3 rounded border ${leftEyeMediumEPassed ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">中号E字符 (6/12)</span>
                <span className="text-sm">
                  {leftEyeMediumECount ?? 0}/5个 {leftEyeMediumEPassed ? '✅' : '❌'}
                </span>
              </div>
            </div>
          </div>

          {/* 近视力测试结果 */}
          <div className={`p-4 rounded-lg border ${nearVisionPassed ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <h3 className="font-semibold text-gray-900 mb-2">双眼近视力测试 (N6)</h3>
            <p className="text-sm">
              40厘米E字符识别：{nearVisionECount ?? 0}/5个 {nearVisionPassed ? '✅' : '❌'} (需≥3个)
            </p>
          </div>
        </div>

        {/* 综合评估 */}
        <div className={`p-4 rounded-lg border ${overallPassed ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
          <div className="flex items-center space-x-2 mb-2">
            <svg className={`w-5 h-5 ${overallPassed ? 'text-green-600' : 'text-yellow-600'}`} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className={`font-medium ${overallPassed ? 'text-green-800' : 'text-yellow-800'}`}>
              综合评估：{overallPassed ? '视力状况良好' : '视力需要关注'}
            </span>
          </div>
          <p className={`text-sm ${overallPassed ? 'text-green-700' : 'text-yellow-700'}`}>
            {overallPassed ? 
              '恭喜！您通过了WHO标准视力筛查，视力状况良好。' : 
              '建议您咨询眼科专业医生，进行进一步的视力检查和评估。'}
          </p>
        </div>

        <div className="flex flex-col gap-3">
          <button
            onClick={handleResultsConfirm}
            className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            确认结果，完成测试
          </button>
          
          <button
            onClick={() => setCurrentStep('carer_correction')}
            className="w-full bg-orange-500 text-white py-3 px-6 rounded-lg hover:bg-orange-600 transition-colors font-medium flex items-center justify-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <span>照护者修正答案</span>
          </button>
        </div>
      </div>
    );
  };

  // 照护者修正页面 - WHO标准测试结果确认
  const renderCarerCorrection = () => {
    // 获取各项测试结果
    const externalEyeCheckPassed = responses.externalEyeCheck !== false;
    
    // 使用新的左右眼测试结果
    const rightEyeLargeECorrect = rightEyeLargeEResults.filter((result, index) => 
      result === largeEChars[index].direction
    ).length;
    const leftEyeLargeECorrect = leftEyeLargeEResults.filter((result, index) => 
      result === largeEChars[index].direction
    ).length;
    
    const rightEyeLargeEPassed = rightEyeLargeECorrect >= 2;
    const leftEyeLargeEPassed = leftEyeLargeECorrect >= 2;
    // const rightEyeMediumEPassed = rightEyeLargeEPassed && (rightEyeMediumECount ?? 0) >= 3;
    // const leftEyeMediumEPassed = leftEyeLargeEPassed && (leftEyeMediumECount ?? 0) >= 3;
    const nearVisionPassed = (nearVisionECount ?? 0) >= 3;

    return (
      <div className="p-6 space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">WHO视力测试照护者确认</h2>
          <p className="text-gray-600">请照护者根据实际观察确认各项测试结果</p>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-gray-900 mb-3">系统评估结果回顾</h3>
          <div className="grid grid-cols-1 gap-3 text-sm">
            <div className={`p-2 rounded ${externalEyeCheckPassed ? 'bg-green-100' : 'bg-red-100'}`}>
              外部眼检：{externalEyeCheckPassed ? '正常' : '异常'} {externalEyeCheckPassed ? '✅' : '❌'}
            </div>
            <div className={`p-2 rounded ${(rightEyeLargeECorrect + leftEyeLargeECorrect) >= 4 ? 'bg-green-100' : 'bg-red-100'}`}>
              大E字符 (6/60)：{rightEyeLargeECorrect + leftEyeLargeECorrect}/8个正确 {(rightEyeLargeECorrect + leftEyeLargeECorrect) >= 4 ? '✅' : '❌'}
            </div>
            <div className={`p-2 rounded ${(rightEyeMediumECount ?? 0) + (leftEyeMediumECount ?? 0) >= 6 ? 'bg-green-100' : 'bg-red-100'}`}>
              中号E字符 (6/12)：{(rightEyeMediumECount ?? 0) + (leftEyeMediumECount ?? 0)}/10个清晰 {(rightEyeMediumECount ?? 0) + (leftEyeMediumECount ?? 0) >= 6 ? '✅' : '❌'}
            </div>
            <div className={`p-2 rounded ${nearVisionPassed ? 'bg-green-100' : 'bg-red-100'}`}>
              近视力 (N6)：{nearVisionPassed ? '清晰识别' : '识别困难'} {nearVisionPassed ? '✅' : '❌'}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {/* 外部眼检确认 */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3">1. 外部眼检结果确认</h4>
            <p className="text-sm text-gray-600 mb-3">请确认老年人的眼部外观是否有异常</p>
            <div className="flex gap-3">
              <button
                onClick={() => setCarerCorrection(prev => ({ ...prev, externalEyeCorrect: true }))}
                className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                  carerCorrection.externalEyeCorrect === true 
                    ? 'bg-green-100 border-green-500 text-green-700' 
                    : 'bg-white border-gray-300 hover:border-green-400'
                }`}
              >
                ✅ 眼部外观正常
              </button>
              <button
                onClick={() => setCarerCorrection(prev => ({ ...prev, externalEyeCorrect: false }))}
                className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                  carerCorrection.externalEyeCorrect === false 
                    ? 'bg-red-100 border-red-500 text-red-700' 
                    : 'bg-white border-gray-300 hover:border-red-400'
                }`}
              >
                ❌ 发现眼部异常
              </button>
            </div>
          </div>

          {/* 大E字符测试确认 */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3">2. 大E字符测试能力确认 (6/60)</h4>
            <p className="text-sm text-gray-600 mb-3">在3米距离，老年人是否能够识别大E字符的开口方向？</p>
            <div className="flex gap-3">
              <button
                onClick={() => setCarerCorrection(prev => ({ ...prev, largeECorrect: true }))}
                className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                  carerCorrection.largeECorrect === true 
                    ? 'bg-green-100 border-green-500 text-green-700' 
                    : 'bg-white border-gray-300 hover:border-green-400'
                }`}
              >
                ✅ 能识别大E字符方向
              </button>
              <button
                onClick={() => setCarerCorrection(prev => ({ ...prev, largeECorrect: false }))}
                className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                  carerCorrection.largeECorrect === false 
                    ? 'bg-red-100 border-red-500 text-red-700' 
                    : 'bg-white border-gray-300 hover:border-red-400'
                }`}
              >
                ❌ 大E字符识别困难
              </button>
            </div>
          </div>

          {/* 中号E字符测试确认 */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3">3. 中号E字符测试能力确认 (6/12)</h4>
            <p className="text-sm text-gray-600 mb-3">在3米距离，老年人是否能够看清中号E字符行？</p>
            <div className="flex gap-3">
              <button
                onClick={() => setCarerCorrection(prev => ({ ...prev, mediumECorrect: true }))}
                className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                  carerCorrection.mediumECorrect === true 
                    ? 'bg-green-100 border-green-500 text-green-700' 
                    : 'bg-white border-gray-300 hover:border-green-400'
                }`}
              >
                ✅ 中号E字符清晰
              </button>
              <button
                onClick={() => setCarerCorrection(prev => ({ ...prev, mediumECorrect: false }))}
                className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                  carerCorrection.mediumECorrect === false 
                    ? 'bg-red-100 border-red-500 text-red-700' 
                    : 'bg-white border-gray-300 hover:border-red-400'
                }`}
              >
                ❌ 中号E字符模糊
              </button>
            </div>
          </div>

          {/* 近视力测试确认 */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3">4. 近视力测试能力确认 (N6)</h4>
            <p className="text-sm text-gray-600 mb-3">在40厘米距离，老年人是否能够清晰识别N6级别的E字符？</p>
            <div className="flex gap-3">
              <button
                onClick={() => setCarerCorrection(prev => ({ ...prev, nearVisionCorrect: true }))}
                className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                  carerCorrection.nearVisionCorrect === true 
                    ? 'bg-green-100 border-green-500 text-green-700' 
                    : 'bg-white border-gray-300 hover:border-green-400'
                }`}
              >
                ✅ 近距离E字符清晰
              </button>
              <button
                onClick={() => setCarerCorrection(prev => ({ ...prev, nearVisionCorrect: false }))}
                className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                  carerCorrection.nearVisionCorrect === false 
                    ? 'bg-red-100 border-red-500 text-red-700' 
                    : 'bg-white border-gray-300 hover:border-red-400'
                }`}
              >
                ❌ 近距离E字符模糊
              </button>
            </div>
          </div>

          <button
            onClick={() => {
              if (carerCorrection.externalEyeCorrect !== null && 
                  carerCorrection.largeECorrect !== null &&
                  carerCorrection.mediumECorrect !== null && 
                  carerCorrection.nearVisionCorrect !== null) {
                handleCarerCorrection(
                  carerCorrection.externalEyeCorrect,
                  carerCorrection.largeECorrect,
                  carerCorrection.mediumECorrect,
                  carerCorrection.nearVisionCorrect
                );
              }
            }}
            disabled={carerCorrection.externalEyeCorrect === null || 
                     carerCorrection.largeECorrect === null ||
                     carerCorrection.mediumECorrect === null ||
                     carerCorrection.nearVisionCorrect === null}
            className="w-full bg-orange-600 text-white py-3 px-6 rounded-lg hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
          >
            确认修正，完成WHO视力评估
          </button>
        </div>
      </div>
    );
  };

  const renderCompleted = () => (
    <div className="text-center p-6">
      <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
      </div>
      <h2 className="text-xl font-bold text-gray-900 mb-2">视力评估完成</h2>
      <p className="text-gray-600">正在转到下一项评估...</p>
    </div>
  );

  // 大E字符测试 (6/60) - 逐个显示
  const renderLargeETest = () => {
    const currentChar = largeEChars[largeEIndex];
    const isLastChar = largeEIndex === largeEChars.length - 1;

    const handleLargeEResponse = (direction: string) => {
      const currentResults = currentEye === 'right' ? rightEyeLargeEResults : leftEyeLargeEResults;
      const newResults = [...currentResults, direction];
      
      if (currentEye === 'right') {
        setRightEyeLargeEResults(newResults);
      } else {
        setLeftEyeLargeEResults(newResults);
      }

      if (isLastChar) {
        // 当前眼的大E测试完成，计算结果
        const correctCount = newResults.filter((result, index) => 
          result === largeEChars[index].direction
        ).length;
        
        const largeEPassed = correctCount >= 2;
        
        if (largeEPassed) {
          // 大E测试通过，继续中号E测试
          // setCurrentTestType('medium_e'); // Removed: not used in current implementation
          setCurrentStep('medium_e_test');
        } else {
          // 大E测试失败，切换到另一只眼或结束测试
          if (currentEye === 'right') {
            // 切换到左眼重新开始大E测试
            setCurrentEye('left');
            setLargeEIndex(0);
            // 保持在large_e_test步骤
          } else {
            // 左右眼大E测试都失败，直接结束远视力测试，跳到近视力
            setCurrentStep('near_vision_test');
          }
        }
      } else {
        setLargeEIndex(prev => prev + 1);
      }
    };

    return (
      <div className="p-6 space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            远视力测试 - {currentEye === 'right' ? '右眼' : '左眼'}大E字符
          </h2>
          {assessmentMode === 'assisted' ? (
            <div className="space-y-2">
              <p className="text-gray-600">请协助者帮助老年人完成测试</p>
              <p className="text-sm text-orange-600">🏠 协助者请保持老年人与屏幕3米距离</p>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-gray-600">请保持3米距离，观察E字符的开口方向</p>
            </div>
          )}
          <div className="bg-blue-100 text-blue-800 px-4 py-2 rounded-lg inline-block mt-2">
            {currentEye === 'right' ? '右眼' : '左眼'} - 第 {largeEIndex + 1} 个字符，共 {largeEChars.length} 个
          </div>
        </div>

        {/* 突出显示的眼部遮盖指导 */}
        <div className="bg-purple-50 border-2 border-purple-300 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-purple-800 text-lg mb-1">
                ⚠️ 重要提示：请遮住{currentEye === 'right' ? '左眼' : '右眼'}
              </h3>
              <p className="text-purple-700 font-medium">
                {assessmentMode === 'assisted' ? 
                  `请协助者用手或纸片完全遮住老年人的${currentEye === 'right' ? '左眼' : '右眼'}，确保只用${currentEye === 'right' ? '右眼' : '左眼'}观看测试` : 
                  `请用手或纸片完全遮住您的${currentEye === 'right' ? '左眼' : '右眼'}，确保只用${currentEye === 'right' ? '右眼' : '左眼'}观看测试`
                }
              </p>
            </div>
          </div>
          <div className="mt-3 bg-white rounded-lg p-3 border border-purple-200">
            <div className="flex items-center space-x-2">
              <span className="text-2xl">{currentEye === 'right' ? '👁️‍🗨️' : '‍🗨️👁️'}</span>
              <span className="font-semibold text-purple-800">
                当前测试：{currentEye === 'right' ? '右眼' : '左眼'}
              </span>
            </div>
          </div>
        </div>
        
        <div className="bg-white border-2 border-gray-200 rounded-lg p-8 text-center">
          <div className="mb-8">
            <p className="text-sm text-gray-600 mb-6">
              {assessmentMode === 'assisted' ? 
                '请协助者让老年人观察下面E字符的开口方向：' : 
                '请观察下面E字符的开口方向：'}
            </p>
            <div className="flex justify-center mb-6">
              <img 
                src={`/images/assessment/${currentChar.file}`}
                alt={`大E字符-${currentChar.label}`}
                className="vision-test-image large-e-char"
                style={{
                  ...visionTestImageStyles.base,
                  ...visionTestImageStyles.largeE
                }}
                onError={(e) => {
                  console.error(`图片加载失败: ${currentChar.file}`);
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
            <p className="text-xs text-gray-500">WHO标准6/60大E字符</p>
          </div>
          
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 max-w-md mx-auto">
              <button
                onClick={() => handleLargeEResponse('left')}
                className="p-4 bg-blue-50 border-2 border-blue-200 rounded-lg hover:bg-blue-100 transition-colors font-medium flex items-center justify-center space-x-2"
              >
                <span className="text-xl">←</span>
                <span>开口向左</span>
              </button>
              <button
                onClick={() => handleLargeEResponse('right')}
                className="p-4 bg-blue-50 border-2 border-blue-200 rounded-lg hover:bg-blue-100 transition-colors font-medium flex items-center justify-center space-x-2"
              >
                <span className="text-xl">→</span>
                <span>开口向右</span>
              </button>
              <button
                onClick={() => handleLargeEResponse('up')}
                className="p-4 bg-blue-50 border-2 border-blue-200 rounded-lg hover:bg-blue-100 transition-colors font-medium flex items-center justify-center space-x-2"
              >
                <span className="text-xl">↑</span>
                <span>开口向上</span>
              </button>
              <button
                onClick={() => handleLargeEResponse('down')}
                className="p-4 bg-blue-50 border-2 border-blue-200 rounded-lg hover:bg-blue-100 transition-colors font-medium flex items-center justify-center space-x-2"
              >
                <span className="text-xl">↓</span>
                <span>开口向下</span>
              </button>
            </div>
            <button
              onClick={() => handleLargeEResponse('unclear')}
              className="w-full max-w-md mx-auto p-3 bg-gray-100 border-2 border-gray-300 rounded-lg hover:bg-gray-200 transition-colors text-gray-700"
            >
              看不清楚
            </button>
          </div>
        </div>
      </div>
    );
  };

  // 中号E字符测试 (6/12) - 整行显示
  const renderMediumETest = () => {
    const handleMediumECount = (count: number) => {
      // 记录当前眼的中号E测试结果
      if (currentEye === 'right') {
        setRightEyeMediumECount(count);
      } else {
        setLeftEyeMediumECount(count);
      }
      
      // const mediumEPassed = count >= 3; // Not used in current logic
      
      if (currentEye === 'right') {
        // 右眼中号E测试完成，显示眼部切换过渡页面
        setCurrentStep('eye_transition');
      } else {
        // 左眼中号E测试完成，显示近视力过渡页面
        setCurrentStep('near_vision_transition');
      }
    };

    return (
      <div className="p-6 space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            远视力测试 - {currentEye === 'right' ? '右眼' : '左眼'}中号E字符
          </h2>
          {assessmentMode === 'assisted' ? (
            <div className="space-y-2">
              <p className="text-gray-600">请协助者帮助老年人观察整行E字符</p>
              <p className="text-sm text-orange-600">🏠 协助者请确保老年人与屏幕保持3米距离</p>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-gray-600">请保持3米距离，观察整行E字符</p>
            </div>
          )}
          <div className="bg-green-100 text-green-800 px-4 py-2 rounded-lg inline-block mt-2">
            {currentEye === 'right' ? '右眼' : '左眼'}大E测试已通过，继续中号E测试
          </div>
        </div>

        {/* 继续保持眼部遮盖指导 */}
        <div className="bg-purple-50 border-2 border-purple-300 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-purple-800 text-lg mb-1">
                ⚠️ 重要提示：继续遮住{currentEye === 'right' ? '左眼' : '右眼'}
              </h3>
              <p className="text-purple-700 font-medium">
                {assessmentMode === 'assisted' ? 
                  `请协助者继续用手或纸片完全遮住老年人的${currentEye === 'right' ? '左眼' : '右眼'}，确保只用${currentEye === 'right' ? '右眼' : '左眼'}观看测试` : 
                  `请继续用手或纸片完全遮住您的${currentEye === 'right' ? '左眼' : '右眼'}，确保只用${currentEye === 'right' ? '右眼' : '左眼'}观看测试`
                }
              </p>
            </div>
          </div>
          <div className="mt-3 bg-white rounded-lg p-3 border border-purple-200">
            <div className="flex items-center space-x-2">
              <span className="text-2xl">{currentEye === 'right' ? '👁️‍🗨️' : '‍🗨️👁️'}</span>
              <span className="font-semibold text-purple-800">
                当前测试：{currentEye === 'right' ? '右眼' : '左眼'}
              </span>
            </div>
          </div>
        </div>
        
        <div className="bg-white border-2 border-gray-200 rounded-lg p-8 text-center">
          <div className="mb-8">
            <p className="text-sm text-gray-600 mb-6">
              {assessmentMode === 'assisted' ? 
                '请协助者让老年人观察下面的E字符行，并记录老年人能看清开口方向的字符数量：' : 
                '请观察下面的E字符行，数一数您能看清开口方向的字符数量：'}
            </p>
            <div className="flex justify-center mb-6">
              <img 
                src="/images/assessment/e_medium_row.png"
                alt="中号E字符行"
                className="vision-test-image medium-e-row"
                style={{
                  ...visionTestImageStyles.base,
                  ...visionTestImageStyles.mediumERow
                }}
                onError={(e) => {
                  console.error('中号E字符行图片加载失败');
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
            <p className="text-xs text-gray-500">WHO标准6/12中号E字符行</p>
          </div>
          
          <div className="space-y-4">
            <p className="font-medium text-gray-900 mb-3">
              {assessmentMode === 'assisted' ? 
                '老年人能看清几个E字符的开口方向？' : 
                '您能看清几个E字符的开口方向？'}
            </p>
            <div className="grid grid-cols-6 gap-2 max-w-md mx-auto">
              {[0, 1, 2, 3, 4, 5].map(num => (
                <button
                  key={num}
                  onClick={() => handleMediumECount(num)}
                  className="p-3 bg-purple-50 border-2 border-purple-200 rounded-lg hover:bg-purple-100 transition-colors font-medium text-lg"
                >
                  {num}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-4">能看清3个或以上表示远视力良好</p>
          </div>
        </div>
      </div>
    );
  };

  // 左右眼切换过渡页面
  const renderEyeTransition = () => {
    const handleContinueToLeftEye = () => {
      // 切换到左眼，重置大E测试索引
      setCurrentEye('left');
      setLargeEIndex(0);
      setCurrentStep('large_e_test');
    };

    return (
      <div className="p-6 space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            右眼测试完成！
          </h2>
          <p className="text-gray-600 mb-4">
            现在开始左眼视力测试
          </p>
        </div>

        <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-green-800 text-lg">右眼测试已完成 ✅</h3>
              <p className="text-green-700 text-sm">
                您已经成功完成右眼的远视力测试
              </p>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6">
          <h3 className="font-semibold text-blue-800 text-lg mb-3">接下来：左眼测试</h3>
          <div className="space-y-3 text-blue-700">
            <div className="flex items-start space-x-2">
              <span className="text-lg mt-1">👁️</span>
              <p><strong>遮盖方式改变：</strong>现在需要遮住右眼，只用左眼观看</p>
            </div>
            <div className="flex items-start space-x-2">
              <span className="text-lg mt-1">📋</span>
              <p><strong>测试流程：</strong>与右眼相同，先大E字符，再中号E字符</p>
            </div>
            <div className="flex items-start space-x-2">
              <span className="text-lg mt-1">📏</span>
              <p><strong>测试距离：</strong>继续保持与屏幕3米距离</p>
            </div>
          </div>
        </div>

        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <svg className="w-5 h-5 text-yellow-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="font-medium text-yellow-800">准备提醒</p>
              <p className="text-yellow-700 text-sm">
                {assessmentMode === 'assisted' ? 
                  '请协助者准备遮住老年人的右眼，确保左眼测试的准确性' : 
                  '请准备好遮住您的右眼，确保左眼测试的准确性'
                }
              </p>
            </div>
          </div>
        </div>

        <button
          onClick={handleContinueToLeftEye}
          className="w-full bg-blue-600 text-white py-4 px-6 rounded-lg hover:bg-blue-700 transition-colors font-medium text-lg flex items-center justify-center space-x-2"
        >
          <span>开始左眼测试</span>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </button>
      </div>
    );
  };

  // 近视力测试过渡页面
  const renderNearVisionTransition = () => {
    const handleContinueToNearVision = () => {
      setCurrentStep('near_vision_test');
    };

    return (
      <div className="p-6 space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            远视力测试完成！
          </h2>
          <p className="text-gray-600 mb-4">
            现在开始近视力测试
          </p>
        </div>

        <div className="bg-purple-50 border-2 border-purple-300 rounded-lg p-6">
          <h3 className="font-semibold text-purple-800 text-lg mb-3">💡 接下来：近视力测试</h3>
          <div className="space-y-2 text-purple-700">
            <p>• 距离调整到40厘米阅读距离</p>
            <p>• 双眼同时观看，无需遮盖</p>
            <p>• 识别小号E字符开口方向</p>
          </div>
        </div>

        <button
          onClick={handleContinueToNearVision}
          className="w-full bg-purple-600 text-white py-4 px-6 rounded-lg hover:bg-purple-700 transition-colors font-medium text-lg flex items-center justify-center space-x-2"
        >
          <span>开始近视力测试</span>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </button>
      </div>
    );
  };
  
  // 近视力测试 (N6标准) - E字符识别
  const renderNearVisionTest = () => {
    const handleNearVisionECount = (count: number) => {
      setNearVisionECount(count);
      setCurrentStep('results');
    };

    return (
      <div className="p-6 space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">近视力测试 (N6标准)</h2>
          {assessmentMode === 'assisted' ? (
            <div className="space-y-2">
              <p className="text-gray-600">请协助者帮助老年人完成近视力测试</p>
              <p className="text-sm text-orange-600">🏠 协助者请确保老年人与屏幕保持40厘米距离</p>
              <p className="text-sm text-blue-600">👁️ 此测试使用双眼进行观看</p>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-gray-600">请保持40厘米阅读距离，观察下方E字符</p>
              <p className="text-sm text-blue-600">👁️ 此测试使用双眼进行观看</p>
            </div>
          )}
        </div>
        
        <div className="bg-white border-2 border-gray-200 rounded-lg p-8 text-center">
          <div className="mb-8">
            <p className="text-sm text-gray-600 mb-6">
              {assessmentMode === 'assisted' ? 
                '请协助者让老年人观察下面的E字符行，并记录老年人能看清开口方向的字符数量：' : 
                '请观察下面的E字符行，数一数您能看清开口方向的字符数量：'}
            </p>
            <div className="flex justify-center mb-6">
              <img 
                src="/images/assessment/near_vision_text.png"
                alt="N6近视力E字符测试"
                className="vision-test-image near-vision-text"
                style={{
                  ...visionTestImageStyles.base,
                  ...visionTestImageStyles.nearVisionText
                }}
                onError={(e) => {
                  console.error('近视力E字符图片加载失败');
                  // 提供E字符备选方案
                  e.currentTarget.style.display = 'none';
                  const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                  if (fallback) fallback.style.display = 'block';
                }}
              />
              {/* 图片加载失败时的备选E字符 */}
              <div className="near-vision-fallback" style={{ display: 'none' }}>
                <div className="text-base text-gray-900 border border-gray-300 p-4 rounded bg-white font-mono">
                  E Ш ≡ III E
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-500">WHO标准N6近视力测试</p>
          </div>
          
          <div className="space-y-4">
            <p className="font-medium text-gray-900 mb-3">
              {assessmentMode === 'assisted' ? 
                '老年人能看清几个E字符的开口方向？' : 
                '您能看清几个E字符的开口方向？'}
            </p>
            <div className="grid grid-cols-6 gap-2 max-w-md mx-auto">
              {[0, 1, 2, 3, 4, 5].map(num => (
                <button
                  key={num}
                  onClick={() => handleNearVisionECount(num)}
                  className="p-3 bg-indigo-50 border-2 border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors font-medium text-lg"
                >
                  {num}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-4">能看清3个或以上表示近视力良好</p>
          </div>
        </div>
      </div>
    );
  };

  const renderStep = () => {
    switch (currentStep) {
      case 'intro': return renderIntro();
      case 'filter1': return renderFilter(true);
      case 'filter2': return renderFilter(false);
      case 'assessment_mode_choice': return renderAssessmentModeChoice();
      case 'external_eye_check': return renderExternalEyeCheck();
      case 'large_e_test': return renderLargeETest();
      case 'medium_e_test': return renderMediumETest();
      case 'eye_transition': return renderEyeTransition();
      case 'near_vision_transition': return renderNearVisionTransition();
      case 'near_vision_test': return renderNearVisionTest();
      case 'results': return renderResults();
      case 'carer_correction': return renderCarerCorrection();
      case 'completed': return renderCompleted();
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto">
        {renderStep()}
      </div>
    </div>
  );
};

export default VisionAssessment;